import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { bankRulesService } from '../services/bankRulesService';
import BankRuleModal from '../components/bank-rules/BankRuleModal';

const {
  FiInfo,
  FiPlus,
  FiSearch,
  FiFilter,
  FiMoreVertical,
  FiEdit2,
  FiTrash2,
  FiArrowUp,
  FiArrowDown,
  FiDollarSign
} = FiIcons;

export default function BankRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await bankRulesService.getRules();
      setRules(data);
    } catch (err) {
      console.error('Error loading rules:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();

    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleToggleAutoAdd = async (id, currentStatus) => {
    try {
      setRules(rules.map((rule) => (
        rule.id === id ? { ...rule, auto_add: !currentStatus } : rule
      )));
      await bankRulesService.toggleAutoAdd(id, currentStatus);
    } catch (err) {
      alert('Failed to update status.');
      loadRules();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        await bankRulesService.deleteRule(id);
        loadRules();
      } catch (err) {
        alert('Failed to delete rule.');
      }
    }
  };

  const handleMovePriority = async (id, direction, currentPriority) => {
    try {
      await bankRulesService.moveRulePriority(id, direction, currentPriority);
      loadRules();
    } catch (err) {
      alert('Failed to reorder rule.');
    }
  };

  const openEditModal = (rule) => {
    setRuleToEdit(rule);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setRuleToEdit(null);
    setIsModalOpen(true);
  };

  const formatConditionText = (conditions) => {
    if (!conditions || conditions.length === 0) return 'No conditions';

    const first = conditions[0];
    const field = first.field.charAt(0).toUpperCase() + first.field.slice(1);
    const operator = first.operator.replace('_', ' ');
    const text = `${field} ${operator} "${first.value}"`;

    return conditions.length > 1
      ? `${text} + ${conditions.length - 1} more`
      : text;
  };

  const formatAppliedAccounts = (rule) => {
    if (!rule.applied_account_ids || rule.applied_account_ids.length === 0) {
      return 'All accounts';
    }

    return `${rule.applied_account_ids.length} selected account${rule.applied_account_ids.length === 1 ? '' : 's'}`;
  };

  const filteredRules = rules.filter((rule) => {
    const searchString = `${rule.name} ${rule.assign_category}`.toLowerCase();
    return searchString.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="mx-auto w-full max-w-[1360px] font-sans text-slate-900">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-[24px] font-semibold tracking-tight text-slate-900 md:text-[26px]">
              Bank Rules
            </h1>
            <SafeIcon
              icon={FiInfo}
              className="cursor-help text-[17px] text-slate-400 transition-colors hover:text-slate-600"
            />
          </div>
          <p className="mt-1 text-[13px] font-normal text-slate-500">
            Automate recurring transaction categorization and review.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-[12px] font-medium text-white shadow-sm shadow-blue-200/70 transition-colors hover:bg-blue-700 active:scale-[0.98]"
        >
          <SafeIcon icon={FiPlus} className="text-[16px]" />
          Create new rule
        </button>
      </div>

      <div className="mb-5 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/80 px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
          <SafeIcon icon={FiDollarSign} className="text-[18px]" />
        </div>
        <div className="min-w-0">
          <h2 className="text-[13px] font-medium text-blue-900">
            Save time with Auto-Add
          </h2>
          <p className="mt-0.5 text-[12px] font-normal leading-5 text-blue-800/80">
            Auto-add rules categorize and clear matching imported transactions without manual review.
          </p>
        </div>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_3px_14px_rgb(15,23,42,0.035)]">
        <div className="flex flex-col justify-between gap-2.5 border-b border-slate-100 bg-slate-50/60 px-3.5 py-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-sm">
            <SafeIcon
              icon={FiSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400"
            />
            <input
              type="text"
              placeholder="Search rules by name or category..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-[12px] font-normal text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
            />
          </div>

          <button
            type="button"
            className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-800"
          >
            <SafeIcon icon={FiFilter} className="text-[15px]" />
            Filter
          </button>
        </div>

        <div className="min-h-[220px] overflow-x-auto">
          {loading ? (
            <div className="flex h-[220px] items-center justify-center">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="flex h-[220px] flex-col items-center justify-center px-6 text-center">
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-50 text-slate-400">
                <SafeIcon icon={FiInfo} className="text-[20px]" />
              </div>
              <h3 className="text-[14px] font-medium text-slate-800">
                No rules found
              </h3>
              <p className="mt-1 text-[12px] font-normal text-slate-500">
                Create a bank rule to start automating your bookkeeping.
              </p>
            </div>
          ) : (
            <table className="w-full min-w-[790px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70">
                  <th className="w-1/5 px-5 py-3 text-[10px] font-medium uppercase tracking-[0.11em] text-slate-500">
                    Rule name
                  </th>
                  <th className="w-1/5 px-5 py-3 text-[10px] font-medium uppercase tracking-[0.11em] text-slate-500">
                    Applied accounts
                  </th>
                  <th className="w-1/4 px-5 py-3 text-[10px] font-medium uppercase tracking-[0.11em] text-slate-500">
                    Condition
                  </th>
                  <th className="px-5 py-3 text-[10px] font-medium uppercase tracking-[0.11em] text-slate-500">
                    Assign to category
                  </th>
                  <th className="px-5 py-3 text-center text-[10px] font-medium uppercase tracking-[0.11em] text-slate-500">
                    Auto-add
                  </th>
                  <th className="px-5 py-3 text-right text-[10px] font-medium uppercase tracking-[0.11em] text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filteredRules.map((rule, index) => (
                  <tr
                    key={rule.id}
                    className="group transition-colors hover:bg-slate-50/70"
                  >
                    <td className="px-5 py-3.5">
                      <span className="text-[13px] font-medium text-slate-800">
                        {rule.name}
                      </span>
                    </td>

                    <td className="max-w-[180px] px-5 py-3.5">
                      <span className="block truncate text-[12px] font-normal text-slate-600">
                        {formatAppliedAccounts(rule)}
                      </span>
                    </td>

                    <td className="max-w-[280px] px-5 py-3.5">
                      <span className="block truncate text-[12px] font-normal text-slate-600">
                        {formatConditionText(rule.conditions)}
                      </span>
                    </td>

                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                        {rule.assign_category}
                      </span>
                    </td>

                    <td className="px-5 py-3.5 text-center">
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={rule.auto_add}
                          onChange={() => handleToggleAutoAdd(rule.id, rule.auto_add)}
                        />
                        <div className={`flex h-5 w-9 items-center rounded-full border p-0.5 transition-all ${
                          rule.auto_add
                            ? 'justify-end border-emerald-500 bg-emerald-50'
                            : 'justify-start border-slate-300 bg-slate-100'
                        }`}>
                          <div className={`h-3.5 w-3.5 rounded-full transition-colors ${
                            rule.auto_add ? 'bg-emerald-500' : 'bg-slate-300'
                          }`} />
                        </div>
                      </label>
                    </td>

                    <td className="relative px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActiveDropdown(activeDropdown === rule.id ? null : rule.id);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-slate-400 transition-all hover:border-slate-200 hover:bg-white hover:text-slate-700 hover:shadow-sm focus:outline-none"
                        aria-label={`Actions for ${rule.name}`}
                      >
                        <SafeIcon icon={FiMoreVertical} className="text-[16px]" />
                      </button>

                      {activeDropdown === rule.id && (
                        <div className="absolute right-5 top-10 z-10 w-48 rounded-xl border border-slate-200 bg-white py-1.5 shadow-lg">
                          <button
                            type="button"
                            onClick={() => openEditModal(rule)}
                            className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12px] font-normal text-slate-700 transition-colors hover:bg-slate-50"
                          >
                            <SafeIcon icon={FiEdit2} className="text-slate-400" />
                            Edit rule
                          </button>

                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => handleMovePriority(rule.id, 'up', rule.priority)}
                              className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12px] font-normal text-slate-700 transition-colors hover:bg-slate-50"
                            >
                              <SafeIcon icon={FiArrowUp} className="text-slate-400" />
                              Move up priority
                            </button>
                          )}

                          {index < filteredRules.length - 1 && (
                            <button
                              type="button"
                              onClick={() => handleMovePriority(rule.id, 'down', rule.priority)}
                              className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12px] font-normal text-slate-700 transition-colors hover:bg-slate-50"
                            >
                              <SafeIcon icon={FiArrowDown} className="text-slate-400" />
                              Move down priority
                            </button>
                          )}

                          <div className="my-1 border-t border-slate-100" />

                          <button
                            type="button"
                            onClick={() => handleDelete(rule.id)}
                            className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12px] font-normal text-rose-600 transition-colors hover:bg-rose-50"
                          >
                            <SafeIcon icon={FiTrash2} className="text-rose-400" />
                            Delete rule
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-2.5 text-center">
          <p className="text-[11px] font-normal text-slate-500">
            Rules are applied in priority order. Use the action menu to reorder them.
          </p>
        </div>
      </section>

      <BankRuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadRules}
        ruleToEdit={ruleToEdit}
      />
    </div>
  );
}